import React from "react";
import { motion } from "framer-motion";

/** SVG viewport — pivot sits precisely at (CX, CY) */
const VIEW_WIDTH = 200;
const VIEW_HEIGHT = 108;
const CX = 100;
const CY = 85;
const R = 58;
const STROKE_WIDTH = 11;
const HUB_RADIUS = 16;
const NEEDLE_LENGTH = 52;

/** Three equal 60° segments spanning the top 180° (score 0 → 100) */
const SEGMENTS = [
	{ start: 0, end: 100 / 3, color: "#ef4444" },
	{ start: 100 / 3, end: (100 * 2) / 3, color: "#f59e0b" },
	{ start: (100 * 2) / 3, end: 100, color: "#22c55e" },
];

/**
 * Map score 0–100 to radians on the TOP semicircle.
 * 0   → π     (left endpoint)
 * 50  → 1.5π  (top apex)
 * 100 → 2π    (right endpoint)
 */
function scoreToRadians(score) {
	return Math.PI + (score / 100) * Math.PI;
}

function pointOnArc(score) {
	const angle = scoreToRadians(score);
	return {
		x: CX + R * Math.cos(angle),
		y: CY + R * Math.sin(angle),
	};
}

function needleTip(score, length = NEEDLE_LENGTH) {
	const angle = scoreToRadians(score);
	return {
		x: length * Math.cos(angle),
		y: length * Math.sin(angle),
	};
}

/**
 * SVG arc along the upper semicircle (sweep 1 = clockwise through top apex).
 */
function describeTopArcPath(startScore, endScore) {
	const start = pointOnArc(startScore);
	const end = pointOnArc(endScore);
	return `M ${start.x.toFixed(3)} ${start.y.toFixed(3)} A ${R} ${R} 0 0 1 ${end.x.toFixed(3)} ${end.y.toFixed(3)}`;
}

export default function ScoreGauge({ value, label, sublabel, index = 0 }) {
	const displayValue = Math.round(Math.min(Math.max(Number(value ?? 0), 0), 100));
	const delay = index * 0.08 + 0.2;
	const tip = needleTip(displayValue);
	const leftScale = pointOnArc(0);
	const rightScale = pointOnArc(100);
	const hubFontSize = displayValue >= 100 ? 11 : displayValue >= 10 ? 13 : 15;

	return (
		<div
			className="score-gauge"
			aria-label={`${label || "Score"}: ${displayValue} out of 100`}
		>
			{label && <p className="score-gauge__label">{label}</p>}

			<div className="score-gauge__chart" style={{ display: 'flex', justifyContent: 'center' }}>
				<svg
					className="score-gauge__svg"
					viewBox={`0 0 ${VIEW_WIDTH} ${VIEW_HEIGHT}`}
					role="img"
					aria-hidden={label ? true : undefined}
					style={{ overflow: "visible", width: '100%', height: 'auto' }}
				>
					{/* Gauge Arc Segments */}
					{SEGMENTS.map((segment, i) => (
						<path
							key={`${segment.start}-${segment.end}`}
							d={describeTopArcPath(segment.start, segment.end)}
							fill="none"
							stroke={segment.color}
							strokeWidth={STROKE_WIDTH}
							strokeLinecap="round" 
						/>
					))}

					{/* Scale Labels */}
					<text
						x={leftScale.x}
						y={leftScale.y + 16}
						textAnchor="middle"
						className="score-gauge__scale"
						fill="#9ca3af"
						fontSize="11"
					>
						0
					</text>
					<text
						x={rightScale.x}
						y={rightScale.y + 16}
						textAnchor="middle"
						className="score-gauge__scale"
						fill="#9ca3af"
						fontSize="11"
					>
						100
					</text>

					{/* Needle + hub — origin completely locked to arc pivot center (CX, CY) */}
					<g transform={`translate(${CX} ${CY})`}>
						<motion.line
							x1="0"
							y1="0"
							initial={{
								x2: needleTip(0).x,
								y2: needleTip(0).y,
							}}
							animate={{
								x2: tip.x,
								y2: tip.y,
							}}
							transition={{ duration: 0.7, delay, ease: "easeOut" }}
							className="score-gauge__needle"
							stroke="#1f2937"
							strokeWidth="3"
							strokeLinecap="round"
						/>

						<circle cx="0" cy="0" r={HUB_RADIUS} className="score-gauge__hub" fill="#111827" />

						{/* Score Value Centered inside Hub */}
						<motion.text
							x="0"
							y="1" 
							textAnchor="middle"
							dominantBaseline="central"
							className="score-gauge__hub-value"
							fill="#ffffff"
							fontSize={hubFontSize}
							fontWeight="bold"
							initial={{ opacity: 0 }}
							animate={{ opacity: 1 }}
							transition={{ duration: 0.4, delay }}
						>
							{displayValue}
						</motion.text>
					</g>
				</svg>
			</div>

			{sublabel && <p className="score-gauge__sublabel">{sublabel}</p>}
		</div>
	);
}

/** Alias for Share of Voice usage */
export function ShareOfVoiceGauge(props) {
	return <ScoreGauge {...props} label={props.label ?? "Share of Voice"} />;
}
